'use strict';

const productImageResolver = require('../utils/productImageResolver');
const isOverlayOpen = require('../utils/isOverlayOpen');

/**
 * PDP bottom sticky bar: visible on desktop, revealed on mobile once the page is scrolled,
 * mirrors price/pack/color from the main PDP and delegates the add-to-cart click to the main button.
 */

function packTypeIconClass(type, isNespressoCompatible) {
	const normalizedType = String(type || '').toLowerCase();

	switch (normalizedType) {
		case 'packtype_capsules':
			return isNespressoCompatible ? 'icon-capsule-NCC' : 'icon-capula-fill';
		case 'packtype_tab':
			return 'icon-tab';
		case 'packtype_tin':
			return 'icon-coffee-can-2-fill';
		case 'packtype_softpack':
			return 'icon-coffe-pack-fill';
		case 'packtype_pods':
			return 'icon-cialda-fill';
		case 'packtype_freshpack':
			return 'icon-flavia';
		case 'packtype_can':
		case 'packtype_sachet':
		case 'packtype_bottle':
		case 'packtype_otherfoodformat':
			return 'icon-food';
		default:
			return '';
	}
}

function findSelectedValue(values) {
	if (!values || !values.length) {
		return null;
	}

	for (let i = 0; i < values.length; i++) {
		if (values[i].selected) {
			return values[i];
		}
	}

	return null;
}

function findPackSelected(product) {
	if (!product || !product.variationAttributes) {
		return null;
	}

	const attrs = product.variationAttributes;
	for (let i = 0; i < attrs.length; i++) {
		if (attrs[i].id === 'VarPack' || attrs[i].id === 'packSize') {
			const sel = findSelectedValue(attrs[i].values);

			if (sel) {
				return sel;
			}
		}
	}

	return null;
}

function initPdpStickyBar() {
	const $bar = $('[data-lvz-pdp-sticky-bar]');
	if (!$bar.length) {
		return;
	}

	const $pd = $bar.closest('.product-detail');
	if (!$pd.length) {
		return;
	}

	const $stickyAtc = $bar.find('[data-lvz-sticky-atc]');
	const $stickyName = $bar.find('[data-lvz-sticky-name]');
	const $stickyThumb = $bar.find('[data-lvz-sticky-thumb]');
	const $stickyPack = $bar.find('[data-lvz-sticky-pack]');
	const $stickyPackIcon = $bar.find('[data-lvz-sticky-pack-icon]');
	const $stickyPackValue = $bar.find('[data-lvz-sticky-pack-value]');
	const $stickyColor = $bar.find('[data-lvz-sticky-color]');
	const $stickyColorDot = $bar.find('[data-lvz-sticky-color-dot]');
	const $stickyColorValue = $bar.find('[data-lvz-sticky-color-value]');
	const isFullSiteSticky = $bar.is('[data-lvz-pdp-sticky-full-site]');

	function getMainAddToCart() {
		return $pd.find('.cart-and-ipay button.add-to-cart').first();
	}

	function getStickyThumbAlt(resolvedImage, product) {
		return resolvedImage.alt || product.productName || $stickyThumb.attr('alt') || '';
	}

	function getStickyThumbTitle(resolvedImage) {
		return resolvedImage.title || $stickyThumb.attr('title') || '';
	}

	function syncThumbFromProduct(product) {
		if (!$stickyThumb.length) {
			return;
		}

		const resolvedImage = productImageResolver.resolveFromImageModel(product.images);
		if (!resolvedImage) {
			return;
		}

		$stickyThumb.attr({
			src: resolvedImage.src,
			alt: getStickyThumbAlt(resolvedImage, product),
			title: getStickyThumbTitle(resolvedImage)
		});
	}

	/** DOM-based fallback, used when no fresh response payload is available. */
	function syncPackDisplay() {
		const $mainPack = $pd.find('[data-pack-select]').first();
		if (!$mainPack.length) {
			$stickyPack.prop('hidden', true);
			window.requestAnimationFrame(syncBarHeight);
			return;
		}

		const iconHtml = $mainPack.find('[data-pack-icon-wrap]').first().html();
		const label = $.trim($mainPack.find('[data-pack-label]').first().text());
		if (!label && !iconHtml) {
			$stickyPack.prop('hidden', true);
			window.requestAnimationFrame(syncBarHeight);

			return;
		}

		$stickyPackIcon.html(iconHtml || '');
		$stickyPackValue.text(label || '');
		$stickyPack.prop('hidden', false);
		window.requestAnimationFrame(syncBarHeight);
	}

	function syncColorDisplay() {
		if (!$stickyColor.length) {
			return;
		}
		const $selected = $pd.find('.color-attribute .swatch-value.selected').first();
		if (!$selected.length) {
			$stickyColor.prop('hidden', true);
			window.requestAnimationFrame(syncBarHeight);
			return;
		}
		// Same inline style as the PDP swatch (swatchHelpers): image layer over the fallback color.
		$stickyColorDot.attr('style', $selected.attr('style') || '');
		const $btn = $selected.closest('button');
		const ariaLabel = $.trim($btn.attr('aria-label') || '');
		const label = ariaLabel || $.trim($selected.attr('data-attr-value') || '');
		$stickyColorValue.text(label);
		$stickyColor.prop('hidden', false);
		window.requestAnimationFrame(syncBarHeight);
	}

	/** Reads from the response payload instead of the DOM to avoid the race with productPackSelect.js. */
	function syncPackFromData(product) {
		if (!$stickyPack.length) {
			return;
		}
		const sel = findPackSelected(product);
		if (!sel) {
			$stickyPack.prop('hidden', true);
			window.requestAnimationFrame(syncBarHeight);
			return;
		}
		const iconClass = packTypeIconClass(sel.type, sel.isNespressoCompatible);
		$stickyPackIcon.html(iconClass ? '<span class="' + iconClass + '" aria-hidden="true"></span>' : '');
		$stickyPackValue.text(sel.displayValue || '');
		$stickyPack.prop('hidden', false);
		window.requestAnimationFrame(syncBarHeight);
	}

	function syncCtaMirror() {
		if (isFullSiteSticky || !$stickyAtc.length) {
			window.requestAnimationFrame(syncBarHeight);
			return;
		}
		const $mainAtc = getMainAddToCart();
		const disabled = !$mainAtc.length || !!$mainAtc.prop('disabled');
		$stickyAtc.prop('disabled', disabled);
		if (disabled) {
			$stickyAtc.attr('aria-disabled', 'true');
		} else {
			$stickyAtc.removeAttr('aria-disabled');
		}
		const atcLabel = $.trim($mainAtc.text());
		if (atcLabel) {
			$stickyAtc.find('.lvz-pdp-sticky-bar__atc-label').text(atcLabel);
		}
		window.requestAnimationFrame(syncBarHeight);
	}

	/** Exposes the bar height as `--lvz-pdp-sticky-bar-height`, used by customStickySidebar for its bottom spacing. */
	function syncBarHeight() {
		$('html').css('--lvz-pdp-sticky-bar-height', ($bar.outerHeight() || 0) + 'px');
	}

	function syncScrollReveal() {
		if (isOverlayOpen()) {
			return;
		}
		$bar.toggleClass('lvz-pdp-sticky-bar--revealed', window.scrollY > 0);
	}

	let scrollTicking = false;
	function onScrollOrResize() {
		if (!scrollTicking) {
			window.requestAnimationFrame(function () {
				scrollTicking = false;
				syncScrollReveal();
				syncBarHeight();
			});
			scrollTicking = true;
		}
	}

	$(window).on('scroll resize', onScrollOrResize);
	$('[data-lvz-product-features-toggle]').on('click', onScrollOrResize);

	$stickyAtc.on('click', function (e) {
		e.preventDefault();
		const $main = getMainAddToCart();
		if ($main.length && !$main.prop('disabled')) {
			$main.trigger('click');
		}
	});

	$('body').on('product:afterAttributeSelect', function (e, response) {
		if (!response || !response.container || !response.container.length) {
			return;
		}
		const $responseContainer = $(response.container);
		if ($responseContainer.closest('#chooseBonusProductModal').length || !$responseContainer.closest($pd).length) {
			return;
		}

		const product = response.data && response.data.product ? response.data.product : null;
		if (product && product.productName) {
			$stickyName.text(product.productName);
		}

		if (product) {
			syncPackFromData(product);
			syncThumbFromProduct(product);
		} else {
			syncPackDisplay();
		}
		syncColorDisplay();

		syncCtaMirror();
		onScrollOrResize();
	});

	$('body').on('product:updateAddToCart', function (e, response) {
		if (!response || !response.$productContainer || !response.$productContainer.length) {
			return;
		}
		if (!response.$productContainer.is($pd)) {
			return;
		}
		syncCtaMirror();
		onScrollOrResize();
	});

	// Deferred so productPackSelect.js updates the trigger label/icon first during the same change event.
	$(document).on('change', '[data-pack-select] select.product__variation-select', function () {
		if ($(this).closest($pd).length) {
			window.setTimeout(function () {
				syncPackDisplay();
				syncCtaMirror();
				onScrollOrResize();
			}, 0);
		}
	});

	syncCtaMirror();
	onScrollOrResize();
}

module.exports = function () {
	initPdpStickyBar();
};
